import React, { useState, useEffect } from 'react';
import { Loader2, Save, Plus, Trash2, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const REVIEW_OPTIONS = [
  { value: 'sample', label: 'Sample (clearly labeled placeholder)' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'verified', label: 'Verified' },
];

const Field = ({ label, children, help }) => (
  <div>
    <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">{label}</label>
    {children}
    {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
  </div>
);

const PointsEditor = ({ points = [], onChange }) => (
  <div className="space-y-2">
    {points.map((p, i) => (
      <div key={i} className="flex gap-2">
        <Input value={p.label} onChange={(e) => onChange(points.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" className="h-10 text-sm" />
        <Input type="number" step="any" value={p.value} onChange={(e) => onChange(points.map((x, j) => (j === i ? { ...x, value: parseFloat(e.target.value) || 0 } : x)))} className="h-10 text-sm w-28" />
        <button type="button" onClick={() => onChange(points.filter((_, j) => j !== i))} className="text-red-600 p-2" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...points, { label: '', value: 0 }])} className="font-bold"><Plus className="h-3.5 w-3.5 mr-1" /> Add point</Button>
  </div>
);

const ChartBlockEditor = ({ block = {}, onChange, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 space-y-3">
      <Field label="Chart title"><Input value={block.title || ''} onChange={(e) => onChange({ ...block, title: e.target.value })} className="h-10" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Review status">
          <Select value={block.review_status || 'sample'} onValueChange={(v) => onChange({ ...block, review_status: v })}>
            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{REVIEW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Source name"><Input value={block.source_name || ''} onChange={(e) => onChange({ ...block, source_name: e.target.value })} className="h-10" /></Field>
      </div>
      <Field label="Source link" help='Leave blank to show "Data source needed" publicly.'><Input value={block.source_url || ''} onChange={(e) => onChange({ ...block, source_url: e.target.value })} className="h-10" /></Field>
      <Field label="Source note (shown under the chart)"><Textarea value={block.source_note || ''} onChange={(e) => onChange({ ...block, source_note: e.target.value })} rows={2} /></Field>
      <Field label="Data points"><PointsEditor points={block.points || []} onChange={(pts) => onChange({ ...block, points: pts })} /></Field>
    </CardContent>
  </Card>
);

const TaxTrackerEditor = () => {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/tax-tracker').then((r) => setContent(r.data)); }, []);
  if (!content) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/admin/tax-tracker', content);
      toast.success('Tax Tracker saved — changes are live on the public page');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 mt-5" data-testid="admin-tax-tracker">
      <Card><CardContent className="p-4 space-y-3">
        <Field label="Intro text (public)"><Textarea value={content.intro || ''} onChange={(e) => setContent({ ...content, intro: e.target.value })} rows={3} data-testid="taxtracker-intro-input" /></Field>
        <Field label="Overall review status" help="Keep as Sample until every figure is verified against official City documents.">
          <Select value={content.review_status || 'sample'} onValueChange={(v) => setContent({ ...content, review_status: v })}>
            <SelectTrigger className="h-10" data-testid="taxtracker-review-select"><SelectValue /></SelectTrigger>
            <SelectContent>{REVIEW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </CardContent></Card>
      <p className="font-bold text-[#1a2744]">Levy history chart</p>
      <ChartBlockEditor block={content.levy_history} onChange={(b) => setContent({ ...content, levy_history: b })} testId="taxtracker-levy-editor" />
      <p className="font-bold text-[#1a2744]">Where a tax dollar goes (pie)</p>
      <ChartBlockEditor block={content.where_dollars_go} onChange={(b) => setContent({ ...content, where_dollars_go: b })} testId="taxtracker-dollar-editor" />
      <p className="font-bold text-[#1a2744]">Operating vs capital chart</p>
      <ChartBlockEditor block={content.operating_vs_capital} onChange={(b) => setContent({ ...content, operating_vs_capital: b })} testId="taxtracker-opcap-editor" />
      <Card><CardContent className="p-4">
        <Field label="Key taxpayer questions (one per line)">
          <Textarea value={(content.key_questions || []).join('\n')} onChange={(e) => setContent({ ...content, key_questions: e.target.value.split('\n').filter(Boolean) })} rows={6} data-testid="taxtracker-questions-input" />
        </Field>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-2">
        <p className="font-bold text-[#1a2744] text-sm">Budget timeline steps</p>
        {(content.timeline || []).map((t, i) => (
          <div key={i} className="flex gap-2">
            <Input value={t.period} onChange={(e) => setContent({ ...content, timeline: content.timeline.map((x, j) => (j === i ? { ...x, period: e.target.value } : x)) })} placeholder="Period" className="h-10 text-sm w-40" />
            <Input value={t.label} onChange={(e) => setContent({ ...content, timeline: content.timeline.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} placeholder="Step" className="h-10 text-sm" />
            <button type="button" onClick={() => setContent({ ...content, timeline: content.timeline.filter((_, j) => j !== i) })} className="text-red-600 p-2" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setContent({ ...content, timeline: [...(content.timeline || []), { period: '', label: '' }] })} className="font-bold"><Plus className="h-3.5 w-3.5 mr-1" /> Add step</Button>
      </CardContent></Card>
      <Button onClick={save} disabled={busy} className="h-12 w-full sm:w-auto bg-[#1a2744] text-white font-bold btn-press" data-testid="taxtracker-save-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Tax Tracker
      </Button>
    </div>
  );
};

const EMPTY_PROJECT = { name: '', community: '', status: 'planned', category: 'roads', cost: '', year: '', summary: '', review_status: 'needs_review', source_name: '', source_url: '', source_note: '', last_updated: '' };

const InfraEditor = () => {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [explaining, setExplaining] = useState(false);

  useEffect(() => { api.get('/infrastructure-tracker').then((r) => setContent(r.data)); }, []);
  if (!content) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...content, projects: (content.projects || []).map((p, i) => ({ ...p, id: p.id || `proj-${i}-${Date.now()}` })) };
      await api.put('/admin/infrastructure-tracker', payload);
      toast.success('Infrastructure Tracker saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const updateProject = (i, patch) => setContent({ ...content, projects: content.projects.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  return (
    <div className="space-y-4 mt-5" data-testid="admin-infra-tracker">
      <Card><CardContent className="p-4">
        <Field label="Intro text (public)"><Textarea value={content.intro || ''} onChange={(e) => setContent({ ...content, intro: e.target.value })} rows={3} data-testid="infra-intro-input" /></Field>
      </CardContent></Card>
      <div className="flex items-center justify-between">
        <p className="font-bold text-[#1a2744]">Projects ({(content.projects || []).length})</p>
        <Button type="button" variant="outline" onClick={() => setContent({ ...content, projects: [...(content.projects || []), { ...EMPTY_PROJECT }] })} className="h-10 font-bold btn-press" data-testid="infra-add-project"><Plus className="h-4 w-4 mr-1" /> Add project</Button>
      </div>
      {(content.projects || []).map((p, i) => (
        <Card key={p.id || i} data-testid="infra-project-editor">
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between gap-2">
              <Field label="Project name"><Input value={p.name} onChange={(e) => updateProject(i, { name: e.target.value })} className="h-10" /></Field>
              <button type="button" onClick={() => setContent({ ...content, projects: content.projects.filter((_, j) => j !== i) })} className="text-red-600 p-2 self-end" aria-label="Remove project"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Community"><Input value={p.community} onChange={(e) => updateProject(i, { community: e.target.value })} className="h-10" /></Field>
              <Field label="Status">
                <Select value={p.status} onValueChange={(v) => updateProject(i, { status: v })}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(content.statuses || { planned: 'Planned', in_progress: 'In Progress', complete: 'Complete', delayed: 'Delayed', needs_review: 'Needs Review' }).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Review status">
                <Select value={p.review_status || 'needs_review'} onValueChange={(v) => updateProject(i, { review_status: v })}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{REVIEW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Cost (e.g. $1.2M)"><Input value={p.cost} onChange={(e) => updateProject(i, { cost: e.target.value })} className="h-10" /></Field>
              <Field label="Target year"><Input value={p.year} onChange={(e) => updateProject(i, { year: e.target.value })} className="h-10" /></Field>
              <Field label="Last updated (date)"><Input type="date" value={p.last_updated || ''} onChange={(e) => updateProject(i, { last_updated: e.target.value })} className="h-10" /></Field>
            </div>
            <Field label="Public summary"><Textarea value={p.summary} onChange={(e) => updateProject(i, { summary: e.target.value })} rows={2} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Source name"><Input value={p.source_name || ''} onChange={(e) => updateProject(i, { source_name: e.target.value })} className="h-10" /></Field>
              <Field label='Source link (blank = "Data source needed")'><Input value={p.source_url || ''} onChange={(e) => updateProject(i, { source_url: e.target.value })} className="h-10" /></Field>
            </div>
            <Field label="Source note"><Input value={p.source_note || ''} onChange={(e) => updateProject(i, { source_note: e.target.value })} className="h-10" /></Field>
          </CardContent>
        </Card>
      ))}
      <Button onClick={save} disabled={busy} className="h-12 w-full sm:w-auto bg-[#1a2744] text-white font-bold btn-press" data-testid="infra-save-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Infrastructure Tracker
      </Button>
    </div>
  );
};

export default function AdminTrackers() {
  const [tab, setTab] = useState('tax');
  return (
    <div data-testid="admin-trackers">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">Tax & Infrastructure Trackers</h1>
      <p className="text-sm text-slate-500 mb-4">Edit the public Tax Tracker and Infrastructure Tracker. Data honesty rules: never present unverified figures as official — keep them marked Sample or Needs Review, and always add source links.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-12 p-1">
          <TabsTrigger value="tax" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="trackers-tab-tax">Tax Tracker</TabsTrigger>
          <TabsTrigger value="infra" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="trackers-tab-infra">Infrastructure</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'tax' ? <TaxTrackerEditor /> : <InfraEditor />}
    </div>
  );
}
